import { Box, Button, Card, CardContent, Chip, IconButton } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { financeApi } from "../lib/api";
import { useBlockedAccessGuard } from "../hooks/useBlockedAccessGuard";
import type { Group } from "../lib/types";
import { queryKeys } from "../lib/queryKeys";
import { CurrencyCodeAutocomplete } from "../components/CurrencyCodeAutocomplete";

type CurrenciesPageProps = {
  group: Group;
  onBlockedAccess?: () => void;
};

export function CurrenciesPage({ group, onBlockedAccess }: CurrenciesPageProps) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("");
  const currenciesQuery = useQuery({ queryKey: queryKeys.currencies(group.id), queryFn: () => financeApi.currencies(group.id) });
  const supportedCurrenciesQuery = useQuery({ queryKey: queryKeys.supportedCurrencies, queryFn: financeApi.supportedCurrencies });
  const providerQuery = useQuery({ queryKey: queryKeys.exchangeRateProvider, queryFn: () => financeApi.exchangeRateProvider() });
  const currencies = currenciesQuery.data || [];
  const providers = providerQuery.data ? [providerQuery.data] : [];

  useBlockedAccessGuard([currenciesQuery.error], onBlockedAccess);

  const createMutation = useMutation({
    mutationFn: (nextCode: string) => financeApi.createCurrency(group.id, nextCode),
    onSuccess: async () => {
      setCode("");
      setStatus("");
      await queryClient.invalidateQueries({ queryKey: queryKeys.currencies(group.id) });
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (targetCode: string) => financeApi.deleteCurrency(group.id, targetCode),
    onSuccess: async () => {
      setStatus("");
      await queryClient.invalidateQueries({ queryKey: queryKeys.currencies(group.id) });
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextCode = code.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(nextCode)) {
      setStatus("Enter a 3-letter ISO currency code.");
      return;
    }
    createMutation.mutate(nextCode);
  };

  return (
    <>
      <header className="topbar">
        <Box>
          <h1>Currencies</h1>
          <p>Currencies enabled for accounts, records, and imports in this group.</p>
        </Box>
      </header>

      <section className="content">
        <Card className="records-panel">
          <CardContent className="records-panel-content">
            <Box className="settings-overview-card-header">
              <div>
                <h2>Exchange rates</h2>
                <p>Provider used for historical conversion rates.</p>
              </div>
            </Box>
            <div className="settings-table-wrapper">
              <table className="settings-table currency-provider-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {providerQuery.isLoading ? (
                    <tr>
                      <td colSpan={4}>Loading provider...</td>
                    </tr>
                  ) : null}
                  {!providerQuery.isLoading && providers.length === 0 ? (
                    <tr>
                      <td colSpan={4}>No exchange-rate provider configured.</td>
                    </tr>
                  ) : null}
                  {providers.map((provider) => (
                    <tr key={provider.key}>
                      <td>
                        <strong>{provider.name}</strong>
                        <span>{provider.key}</span>
                      </td>
                      <td><Chip label="Active" size="small" color="primary" variant="outlined" /></td>
                      <td>
                        <a href={provider.url} target="_blank" rel="noreferrer">{provider.url}</a>
                      </td>
                      <td>{provider.description || "Historical currency conversion is fetched from this provider."}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
          <CardContent className="records-panel-content">
            <form className="currency-settings-form" onSubmit={submit}>
              <CurrencyCodeAutocomplete
                label="Currency code"
                size="small"
                value={code}
                currencies={supportedCurrenciesQuery.data || []}
                helperText="Or enter any 3-letter ISO code."
                onChange={setCode}
              />
              <Button variant="contained" size="small" type="submit" disabled={createMutation.isPending}>
                Add
              </Button>
            </form>
            {status ? <p className="status error">{status}</p> : null}
            {currenciesQuery.isLoading ? <p className="status">Loading currencies...</p> : null}
            <div className="settings-table-wrapper">
              <table className="settings-table currency-settings-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!currenciesQuery.isLoading && currencies.length === 0 ? (
                    <tr>
                      <td colSpan={4}>No currencies added yet.</td>
                    </tr>
                  ) : null}
                  {currencies.map((currency) => (
                    <tr key={currency.code}>
                      <td>
                        <span className="currency-code-cell">
                          <span className="currency-icon" aria-hidden="true">{currency.symbol}</span>
                          <strong>{currency.code}</strong>
                        </span>
                      </td>
                      <td>{currency.name}</td>
                      <td>
                        <span className="currency-settings-badges">
                          {currency.is_base ? <Chip label="Base" size="small" /> : null}
                          {currency.is_used ? <Chip label="In use" size="small" /> : null}
                        </span>
                      </td>
                      <td>
                        <IconButton
                          className="records-icon-button"
                          type="button"
                          aria-label={`Remove ${currency.code}`}
                          title={`Remove ${currency.code}`}
                          disabled={currency.is_base || currency.is_used || deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(currency.code)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </section>
    </>
  );
}
